import React, { FC, ReactNode, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useDrop } from 'react-dnd';
import { TableComponentProps, TableKey } from '../../types';
import { DragTypes } from '~ComponentsImproved/TSR/Types';
import {
  TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType,
  TablesAndFloorPlansQuery_venue_floorPlans_markers as MarkerType,
  TablesAndFloorPlansQuery_venue_floorPlans_structures as StructureType,
} from '~Scenes/Handy/__queries__';
import Icon from '~ComponentsImproved/Icon';
import UpIcon from '~Assets/icons/expand_less.svg';
import { InnerWrapper, HANDY_SCALE_FACTOR } from './index';
import Marker from '~Components/Marker';
import Structure from '~Components/Structure';

/* Scroll areas for horizontal scrolling when dragging a cheque */
const ScrollHoverAreaHorizontal = styled.div<{ right?: boolean; isOver: boolean }>`
  position: fixed;
  top: 50%;
  left: ${({ right }) => (right ? 'auto' : '0')};
  right: ${({ right }) =>
    right ? 'calc(100vw)' /* We need to take into account the ChequePanel which is 80px */ : '0'};
  width: 40px;
  height: 40px;
  margin-top: -20px;
  transition: background-color 0.2s ease-in-out;
  background-color: ${({ isOver }) => (isOver ? 'var(--colorBlack)' : 'var(--colorBlueGray1300)')};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

/* Scroll areas for vertical scrolling when dragging a cheque */
const ScrollHoverAreaVertical = styled.div<{ bottom?: boolean; isOver: boolean }>`
  position: fixed;
  top: ${({ bottom }) => (bottom ? 'auto' : '0')}; /* Top or bottom positioning */
  bottom: ${({ bottom }) => (bottom ? '0' : 'auto')};
  left: 0;
  width: calc(100vw - 80px); /* Adjust width to fit screen */
  height: 40px;
  transition: background-color 0.2s ease-in-out;
  background-color: ${({ isOver }) => (isOver ? 'var(--colorBlack)' : 'var(--colorBlueGray1300)')};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const DragScrollWrapper = styled.div<{ mapWidth: number; mapHeight: number }>`
  position: relative;
  overflow: auto;
  width: ${({ mapWidth }) => mapWidth}px;
  min-height: calc(100vh - 200px);
  max-height: calc(100vh - 200px);
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface DragChequeHandlerProps {
  tables: TableType[];
  markers: MarkerType[];
  structures: StructureType[];
  tableComponent: ({ table, keyType, isMapView }: TableComponentProps) => ReactNode;
  mapWidth: number;
  mapHeight: number;
}

/* Component responsible for dragging cheques in TableMap */
const DragChequeHandler: FC<DragChequeHandlerProps> = ({
  tables,
  markers,
  structures,
  tableComponent,
  mapWidth,
  mapHeight,
}) => {
  const dragWrapperRef = useRef<HTMLDivElement>(null);

  /* Drop areas for horizontal scrolling (left/right) */
  const [{ isOver: isOverLeft }, leftHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isOver: isOverRight }, rightHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  /* Drop areas for vertical scrolling (top/bottom) */
  const [{ isOver: isOverBottom }, bottomHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isOver: isOverTop }, topHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  /* Handles animation for scrolling when dragging a cheque */
  useEffect(() => {
    let animationFrameId: number | null = null;
    const scrollDivRef = dragWrapperRef.current;

    const animateScroll = () => {
      if (scrollDivRef) {
        if (isOverLeft) {
          scrollDivRef.scrollLeft -= 10;
        }
        if (isOverRight) {
          scrollDivRef.scrollLeft += 10;
        }
        if (isOverBottom) {
          scrollDivRef.scrollTop += 10;
        }
        if (isOverTop) {
          scrollDivRef.scrollTop -= 10;
        }
      }
      animationFrameId = requestAnimationFrame(animateScroll);
    };

    if (isOverLeft || isOverRight || isOverTop || isOverBottom) {
      animationFrameId = requestAnimationFrame(animateScroll);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOverLeft, isOverRight, isOverTop, isOverBottom]);

  return (
    <>
      <DragScrollWrapper ref={dragWrapperRef} mapWidth={mapWidth} mapHeight={mapHeight}>
        {tables.map((table) => (
          <InnerWrapper key={table.id} posX={table.posX} posY={table.posY}>
            {tableComponent({
              table,
              keyType: TableKey['floor_plan'],
              isMapView: true,
            })}
          </InnerWrapper>
        ))}
        {markers.map((marker) => (
          <InnerWrapper key={marker.id} posX={marker.posX} posY={marker.posY}>
            <Marker icon={marker.icon} size={marker.size} isHandy />
          </InnerWrapper>
        ))}
        {structures.map((structure) => {
          return (
            <Structure
              key={structure.id}
              posX={structure.posX}
              posY={structure.posY}
              width={structure.width}
              height={structure.height}
              rotation={(structure as any).rotation || 0}
              isHandy
            />
          );
        })}
      </DragScrollWrapper>

      {/* Horizontal and Vertical Scroll Areas */}
      <ScrollHoverAreaHorizontal ref={leftHover} isOver={isOverLeft}>
        <Icon src={UpIcon} style={{ transform: 'rotate(-90deg)' }} />
      </ScrollHoverAreaHorizontal>
      <ScrollHoverAreaHorizontal ref={rightHover} right isOver={isOverRight}>
        <Icon src={UpIcon} style={{ transform: 'rotate(90deg)' }} />
      </ScrollHoverAreaHorizontal>

      <ScrollHoverAreaVertical ref={topHover} isOver={isOverTop}>
        <Icon src={UpIcon} />
      </ScrollHoverAreaVertical>
      <ScrollHoverAreaVertical ref={bottomHover} bottom isOver={isOverBottom}>
        <Icon src={UpIcon} style={{ transform: 'rotate(180deg)' }} />
      </ScrollHoverAreaVertical>
    </>
  );
};

export default DragChequeHandler;
